import isPropValid from '@emotion/is-prop-valid';
import type { NextPage } from 'next';
import type { AppProps } from 'next/app';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { ReactElement, ReactNode, useEffect, useMemo, useState } from 'react';
import { StyleSheetManager } from 'styled-components';

import { camelizeDeep } from '@vpr/restful';
import { GoogleTagManager as GTM } from '@vpr/tracking';

import { ThemeProvider } from '@vimeo/core/components/ThemeProvider';
import { useDatadogPageTracking, useDatadogSetUser } from '@vimeo/core/libs/tracking';
import { GctlProvider } from '@vimeo/gctl-api/hooks/GctlProvider';
import { PicoProvider, usePageDisplayedTracking } from '@vimeo/picox';
import { User, Viewer, ViewerContext, useSetUpJwtPolling } from '@vimeo/viewer';

import 'src/_layout/app.scss';
import ErrorPage from 'src/_components/Error';
import Footer from 'src/_components/Navigations/Footer';
import TopNavigation from 'src/_components/Navigations/TopNavigation';
import TranslationBanner from 'src/_components/TranslationBanner';

declare global {
  interface Window {
    devsite?: {
      app?: {
        id?: number;
        name?: string;
      };
      user?: User;
      xsrft?: string;
      vimeo_url?: string;
      exception: {
        code: number;
        message: string | null;
      } | null;
    };
    bootstrap_data?: {
      viewer?: Viewer;
    };
  }
}

export type NextPageWithLayout = NextPage & {
  getLayout?: (page: ReactElement) => ReactNode;
};

type AppPropsWithLayout = AppProps & {
  Component: NextPageWithLayout;
};

let viewerPromise: null | Promise<Viewer> = null;
if (typeof window !== 'undefined') {
  viewerPromise = fetch(`${window.devsite.vimeo_url}/_next/viewer`, {
    method: 'GET',
    credentials: 'include',
  })
    .then((res) => res.json())
    .then(camelizeDeep);
}

function MyApp({ Component, pageProps }: AppPropsWithLayout) {
  const [isClient, setIsClient] = useState(false);
  const [viewer, setViewer] = useState<Viewer | null>(null);
  const router = useRouter();

  const viewerId = viewer?.user?.id?.toString() ?? null;
  const vuid = viewer?.vuid ?? null;

  const picoIdentifiers = useMemo(
    () => ({
      user_id: viewerId,
      vuid,
    }),
    [viewerId, vuid],
  );

  useDatadogSetUser(viewer);
  useDatadogPageTracking();

  /* useEffects only run in browser */
  useEffect(function determineServerOrClient() {
    setIsClient(true);
  }, []);

  // on mount
  useEffect(
    function hydrateViewer() {
      if (!isClient) return;
      viewerPromise?.then((_viewer) => {
        setViewer(_viewer);
        // Add viewer to window for legacy packages
        window.bootstrap_data = { viewer: _viewer };
      });
      GTM.init(window?._gtm || []);
    },
    [isClient],
  );

  // Refresh JWT
  useSetUpJwtPolling({
    viewer,
    onSuccess: (jwt) => setViewer((prev) => prev && { ...prev, jwt }),
  });

  return (
    <StyleSheetManager
      shouldForwardProp={(prop, target) => (typeof target === 'string' ? isPropValid(prop) : true)}
    >
      <Head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="initial-scale=1.0, width=device-width" />
        <meta httpEquiv="X-UA-Compatible" content="IE=edge"></meta>
      </Head>
      {isClient && (
        <GctlProvider
          xVimeoPage={router.pathname}
          jwt={viewer?.jwt}
          baseUrl={viewer ? `https://${viewer.apiUrl}` : ''}
          locale={viewer?.locale}
        >
          <ViewerContext.Provider value={viewer}>
            <PicoProvider identifiers={viewer !== null ? picoIdentifiers : null}>
              <GlobalPageDisplayedTracker />
              <ThemeProvider hasThemeSupport={pageProps.hasThemeSupport}>
                {window.devsite?.exception ? (
                  <>
                    <TopNavigation />
                    <ErrorPage error={window.devsite?.exception} />
                    <Footer />
                  </>
                ) : Component.getLayout ? (
                  Component.getLayout(<Component {...pageProps} />)
                ) : (
                  <>
                    <TopNavigation />
                    <TranslationBanner />
                    <Component {...pageProps} />
                    <Footer />
                  </>
                )}
              </ThemeProvider>
            </PicoProvider>
          </ViewerContext.Provider>
        </GctlProvider>
      )}
    </StyleSheetManager>
  );
}

function GlobalPageDisplayedTracker() {
  usePageDisplayedTracking();
  return null;
}

export default MyApp;
